import { fn } from '@ember/helper';
import Divider from 'tio-reimagined/components/divider';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { and, eq, not } from 'tio-ui/utilities';
import { Section } from 'tio-reimagined/components/layout';
import { t } from 'ember-intl';
import { divide } from 'tio-ui/math';
import Button from 'tio-reimagined/components/button';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import DescriptionList from 'tio-ui/components/description-list';
import LimitsSummary from 'tio-common/components/tuition-assistance/limits-summary';
import ReimbursementCalculationIfApplicable from 'tio-common/components/tuition-assistance/reimbursement-calculation-if-applicable';
import ReimbursementPopoverTag from 'tio-common/components/tas/reimbursement-popover-tag';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import SelectableCard from 'tio-ui/components/selectable-card';
import { PhNotePencil, PhPlus, PhTrash } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type EmployeeModel from 'tio-data/models/employee';
import type { ReceivedScholarship } from 'tio-common/types/tuition-assistance';

interface Scholarship extends ReceivedScholarship {
  id?: string;
}

interface StepPaymentTypeSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    programTemplate: TasProgramTemplateModel;
    employee: EmployeeModel;
    paymentMethod: string;
    shouldFetchLimits: boolean;
    scholarships: Scholarship[];
    scholarshipReceived: { label: string };
    onSetPaymentMethod: (value: string) => void;
    onToggleScholarshipDrawer: () => void;
    onOpenDeleteScholarship: (scholarship: Scholarship) => void;
    onOpenEditScholarship: (scholarship: Scholarship) => void;
    isTypeTwoProgram: boolean;
    onNext: () => void;
    hasAttempted: boolean;
    isComplete: boolean;
  };
}

export default class StepPaymentType extends Component<StepPaymentTypeSignature> {
  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get isCourseCash() {
    return this.args.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isReimbursement() {
    return this.args.paymentMethod === 'TAS.AssistanceManner.REIMBURSEMENT';
  }

  <template>
    <Section>
      <:header>
        <div class="tio-row-tight">
          <span
            class="tio-inline-row-center w-7 h-7 rounded-full tio-bg-brand text-white text-xs font-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.payment_method.default"}}</span>
        </div>
      </:header>
      <:body>
        <p class="text-sm tio-fg-muted mb-4">{{t "tas.guided.steps.payment_type_description"}}</p>

        {{! Payment method selection }}
        {{#if @programTemplate.isDirectPayProgram}}
          <div class="grid grid-cols-2 gap-4 mb-6">
            <SelectableCard
              @isSelected={{this.isCourseCash}}
              @onChange={{@onSetPaymentMethod}}
              @value="TAS.AssistanceManner.COURSE_CASH"
              @label={{t "tas.payment_method.course_cash"}}
              @description={{t "tas.payment_method.course_cash_description"}}
            >
              <:label>
                <span aria-hidden="true">{{htmlSafe (t "common.tas.course_cash.default")}}</span>
                <span class="sr-only">{{t "common.tas.course_cash.label"}}</span>
              </:label>
            </SelectableCard>
            <SelectableCard
              @isSelected={{this.isReimbursement}}
              @onChange={{@onSetPaymentMethod}}
              @value="TAS.AssistanceManner.REIMBURSEMENT"
              @label={{t "tas.payment_method.reimbursement"}}
              @description={{t "tas.payment_method.reimbursement_description"}}
            />
          </div>
        {{/if}}

        <Divider class="my-4" />

        {{! Benefits summary }}
        <LimitsSummary
          @application={{@application}}
          @employee={{@employee}}
          @labelClass="col-span-5"
          @valueClass="col-span-2 text-violet-800"
          @paymentMethod={{@paymentMethod}}
          @shouldFetchLimits={{@shouldFetchLimits}}
          class="my-4"
          as |Summary|
        >
          <div class="w-full grid grid-cols-7 gap-2 mt-3">
            {{#unless Summary.isUnlimitedMaximumAnnualBenefit}}
              <Summary.MaxAnnualBenefitLineItem />
            {{/unless}}

            {{#if Summary.hasLimitsErrorForEmployee}}
              <Summary.RemainingBenefitLineItem />
            {{/if}}
            <Summary.ApplicationAmountRequestedWithoutScholarshipsLineItem
              @valueClass="text-tio-gray-800 col-span-2"
            />
            {{#if (and @programTemplate.scholarshipsAreVisible (not Summary.isCredits))}}
              <Summary.ScholarshipAmountLineItem
                @valueClass="text-tio-gray-800 !font-normal col-span-2"
              />
              <button
                type="button"
                class="text-sm text-link font-medium tio-inline-row gap-1 hover:underline col-span-full"
                data-test-add-scholarship-drawer
                {{on "click" @onToggleScholarshipDrawer}}
              >
                <PhPlus @size="16" />
                {{t "tas.new.scholarships.add_scholarship"}}
              </button>
              {{#if @scholarships.length}}
                <div class="col-span-full -mt-0.5">
                  {{#each @scholarships as |scholarship|}}
                    <div class="tio-row-between py-0.5 px-2">
                      <div class="tio-row-tight min-w-0">
                        <span
                          class="text-sm tio-fg-secondary"
                        >{{scholarship.scholarshipName}}</span>
                        <span class="text-xs tio-fg-disabled">{{safeFormatNumber
                            (divide scholarship.scholarshipAmount 100)
                            style="currency"
                            currency="USD"
                          }}</span>
                      </div>
                      <div class="flex items-center gap-0.5">
                        <button
                          type="button"
                          class="p-0.5 rounded tio-fg-disabled hover:tio-fg-secondary hover:tio-bg-sunken transition-colors"
                          data-test-edit-scholarship
                          {{on "click" (fn @onOpenEditScholarship scholarship)}}
                        >
                          <PhNotePencil @size="14" />
                        </button>
                        <button
                          type="button"
                          class="p-0.5 rounded tio-fg-disabled hover:tio-fg-danger-body hover:tio-bg-danger-soft-hover transition-colors"
                          data-test-delete-scholarship
                          {{on "click" (fn @onOpenDeleteScholarship scholarship)}}
                        >
                          <PhTrash @size="14" />
                        </button>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            {{/if}}
            <Summary.LimitsAlertForEmployeeIfApplicable class="text-sm col-span-full" />
            <ReimbursementCalculationIfApplicable @application={{@application}} />
            <Divider class="my-2 col-span-7" />

            {{#if @programTemplate.calculateTotalByGPA}}
              <Summary.ApplicationAmountBasedOnGPALineItem />
            {{else}}
              <Summary.ApplicationAmountRequestedLineItem />
            {{/if}}
          </div>
        </LimitsSummary>

        {{! Payment Method Totals (Course Cash vs Reimbursement breakdown) }}
        {{#if @programTemplate.isDirectPayProgram}}
          <div class="mt-4">
            <p class="text-sm font-semibold mb-2">{{t
                "tas.payment_method.payment_method_totals"
              }}</p>
            <DescriptionList @compact={{true}} as |dl|>
              {{#if (eq @paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
                <dl.Group>
                  <dl.Term>
                    <CourseCashTag
                      @isDirectPayProgram={{true}}
                      @size="sm"
                      class="text-inherit"
                      @buttonClass="text-tio-gray-700"
                    />
                  </dl.Term>
                  <dl.Details class="pl-1">
                    {{safeFormatNumber
                      (divide @application.requestedCourseCashSubtotal 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                </dl.Group>
                <dl.Group>
                  <dl.Term class="text-xs font-normal md:indent-8">
                    {{t "tas.payment_method.scholarships"}}
                  </dl.Term>
                  <dl.Details>
                    -{{safeFormatNumber
                      (divide @application.scholarshipsTotal 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                </dl.Group>
                <div class="max-w-xs flex md:justify-end">
                  <Divider class="my-2 w-46.25" />
                </div>
                <dl.Group>
                  <dl.Term class="text-xs font-normal md:text-right">
                    {{htmlSafe
                      (t
                        "common.tas.course_cash.total"
                        visuallyStyledCourseCash=(t "common.tas.course_cash.default")
                        plainCourseCash=(t "common.tas.course_cash.label")
                      )
                    }}
                  </dl.Term>
                  <dl.Details class="pl-1">
                    {{safeFormatNumber
                      (divide @application.requestedCourseCashSubtotalLessScholarships 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                </dl.Group>
                <div class="max-w-xs">
                  <Divider class="my-2" />
                </div>
              {{/if}}
              <dl.Group>
                <dl.Term><ReimbursementPopoverTag @size="sm" /></dl.Term>
                {{#if (eq @paymentMethod "TAS.AssistanceManner.REIMBURSEMENT")}}
                  <dl.Details class="pl-1">
                    {{safeFormatNumber
                      (divide @application.requestedTotalAllCourses 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                {{else}}
                  <dl.Details class="pl-1">
                    {{safeFormatNumber
                      (divide @application.requestedNonCourseCashSubtotal 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                {{/if}}
              </dl.Group>
              {{#if (eq @paymentMethod "TAS.AssistanceManner.REIMBURSEMENT")}}
                <dl.Group>
                  <dl.Term class="text-xs font-normal md:indent-8">
                    {{t "tas.payment_method.scholarships"}}
                  </dl.Term>
                  <dl.Details>
                    -{{safeFormatNumber
                      (divide @application.scholarshipsTotal 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dl.Details>
                </dl.Group>
              {{/if}}
              <div class="max-w-xs">
                <Divider class="my-2" />
              </div>
              <dl.Group>
                <dl.Term class="text-xs md:text-right font-normal">
                  {{t "tas.payment_method.payment_total"}}
                </dl.Term>
                <dl.Details class="font-semibold pl-1">
                  {{safeFormatNumber
                    (divide @application.requestedTotal 100)
                    style="currency"
                    currency="USD"
                  }}
                </dl.Details>
              </dl.Group>
            </DescriptionList>
          </div>
        {{/if}}

        {{! Next button }}
        <div class="flex pt-8">
          <Button @intent="primary" data-test-next-step {{on "click" @onNext}}>
            {{t "common.next"}}
          </Button>
        </div>
      </:body>
    </Section>
  </template>
}
